import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import { IconButton } from '@atlaskit/button/new';
import { Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { FollowUpQuestions } from './FollowUpQuestions.tsx';
import { RovoInput } from './RovoInput.tsx';
import { SourcesSection } from './SourcesSection.tsx';
import type { FAQData } from './PopularQuestions.tsx';

type FAQEntry = NonNullable<FAQData['entries']>[number];

type QuestionItemProps = {
	question: FAQEntry;
	isExpanded: boolean;
	onToggle: (id: string) => void;
	onFollowUp?: (followUpQuestion: string, parentQuestion: FAQEntry) => void;
	onAskRovo?: (customQuestion: string, parentQuestion: FAQEntry) => void;
};

type FeedbackButtonsProps = {
	vote: 'up' | 'down' | null;
	onVoteUp: (e: React.MouseEvent) => void;
	onVoteDown: (e: React.MouseEvent) => void;
	upLabel: string;
	downLabel: string;
};

const FeedbackButtons = ({
	vote,
	onVoteUp,
	onVoteDown,
	upLabel,
	downLabel,
}: FeedbackButtonsProps) => (
	<>
		<IconButton
			icon={(iconProps) => (
				<ThumbsUpIcon
					{...iconProps}
					color={vote === 'up' ? token('color.icon.accent.blue') : token('color.icon.subtle')}
				/>
			)}
			label={upLabel}
			appearance="subtle"
			spacing="compact"
			onClick={onVoteUp}
		/>
		<IconButton
			icon={(iconProps) => (
				<ThumbsDownIcon
					{...iconProps}
					color={vote === 'down' ? token('color.icon.accent.blue') : token('color.icon.subtle')}
				/>
			)}
			label={downLabel}
			appearance="subtle"
			spacing="compact"
			onClick={onVoteDown}
		/>
	</>
);

const QuestionHeader = ({
	questionText,
	isExpanded,
	onToggle,
}: {
	questionText: string;
	isExpanded: boolean;
	onToggle: () => void;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [questionVote, setQuestionVote] = useState<'up' | 'down' | null>(null);

	const handleQuestionThumbsUp = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			setQuestionVote((prev) => (prev === 'up' ? null : 'up'));
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'QuestionThumbsUp',
					source: 'spaceOverviewFAQ',
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handleQuestionThumbsDown = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			setQuestionVote((prev) => (prev === 'down' ? null : 'down'));
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'QuestionThumbsDown',
					source: 'spaceOverviewFAQ',
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	return (
		<div
			css={[
				styles.questionRow,
				isExpanded ? styles.questionRowExpanded : styles.questionRowCollapsed,
			]}
		>
			<Pressable
				xcss={styles.questionTrigger}
				onClick={onToggle}
				aria-expanded={isExpanded}
				aria-label={formatMessage(isExpanded ? i18n.collapseQuestion : i18n.expandQuestion, {
					question: questionText,
				})}
			/>
			<div css={styles.questionIcon}>
				<RovoChatIcon label="" color={token('color.icon.subtle')} />
			</div>
			<div css={styles.questionText}>
				<Text size="medium" color="color.text.subtle">
					{questionText}
				</Text>
			</div>
			<div
				css={[
					styles.questionFeedbackButtons,
					questionVote === null && styles.hideQuestionFeedbackButtons,
				]}
			>
				<FeedbackButtons
					vote={questionVote}
					onVoteUp={handleQuestionThumbsUp}
					onVoteDown={handleQuestionThumbsDown}
					upLabel={formatMessage(i18n.thumbsUpQuestion)}
					downLabel={formatMessage(i18n.thumbsDownQuestion)}
				/>
			</div>
			<div css={styles.chevron}>
				{isExpanded ? (
					<ChevronUpIcon
						label={formatMessage(i18n.collapseQuestion, { question: questionText })}
						color={token('color.icon.subtle')}
					/>
				) : (
					<ChevronDownIcon
						label={formatMessage(i18n.expandQuestion, { question: questionText })}
						color={token('color.icon.subtle')}
					/>
				)}
			</div>
		</div>
	);
};

export const QuestionItem = ({
	question,
	isExpanded,
	onToggle,
	onFollowUp,
	onAskRovo,
}: QuestionItemProps) => {
	const { formatMessage } = useIntl();
	const [sourcesExpanded, setSourcesExpanded] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [answerVote, setAnswerVote] = useState<'up' | 'down' | null>(null);

	const handleToggle = useCallback(() => {
		onToggle(question.question);
	}, [onToggle, question.question]);

	const handleFollowUp = useCallback(
		(followUpQuestion: string) => {
			onFollowUp?.(followUpQuestion, question);
		},
		[onFollowUp, question],
	);

	const handleAskRovo = useCallback(
		(message: string) => {
			onAskRovo?.(message, question);
		},
		[onAskRovo, question],
	);

	const toggleSources = useCallback(() => {
		setSourcesExpanded((prev) => !prev);
	}, []);

	const handleAnswerThumbsUp = useCallback(() => {
		setAnswerVote((prev) => (prev === 'up' ? null : 'up'));
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'AnswerThumbsUp',
				source: 'spaceOverviewFAQ',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleAnswerThumbsDown = useCallback(() => {
		setAnswerVote((prev) => (prev === 'down' ? null : 'down'));
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'AnswerThumbsDown',
				source: 'spaceOverviewFAQ',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	return (
		<div css={styles.cardWrapper}>
			<QuestionHeader
				questionText={question.question}
				isExpanded={isExpanded}
				onToggle={handleToggle}
			/>

			{isExpanded && (
				<div css={styles.expandedContent}>
					<Stack space="space.200">
						<Stack space="space.100">
							<div css={styles.sectionLabel}>
								<Text size="small" weight="medium" color="color.text.subtlest">
									{formatMessage(i18n.answerLabel)}
								</Text>
								<div
									css={[
										styles.answerFeedbackButtons,
										answerVote === null && styles.hideAnswerFeedbackButtons,
									]}
								>
									<FeedbackButtons
										vote={answerVote}
										onVoteUp={handleAnswerThumbsUp}
										onVoteDown={handleAnswerThumbsDown}
										upLabel={formatMessage(i18n.thumbsUpAnswer)}
										downLabel={formatMessage(i18n.thumbsDownAnswer)}
									/>
								</div>
							</div>
							<Text size="medium" color="color.text.subtle">
								{question.answer}
							</Text>
						</Stack>

						<div>
							<SourcesSection
								sources={question.sources}
								isExpanded={sourcesExpanded}
								onToggle={toggleSources}
							/>

							<FollowUpQuestions
								questions={question.followUpQuestions}
								onFollowUp={handleFollowUp}
							/>
						</div>

						<RovoInput onSend={handleAskRovo} />
					</Stack>
				</div>
			)}
		</div>
	);
};

const i18n = defineMessages({
	expandQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.expand',
		defaultMessage: 'Expand question: {question}',
		description: 'Accessible label for the button to expand a popular question.',
	},
	collapseQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.collapse',
		defaultMessage: 'Collapse question: {question}',
		description: 'Accessible label for the button to collapse a popular question.',
	},
	answerLabel: {
		id: 'space-overview.smart-space-overview.popular-questions.answer-label',
		defaultMessage: 'Answer',
		description: 'Label for the answer section of a popular question.',
	},
	thumbsUpQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.thumbs-up',
		defaultMessage: 'Good question',
		description: 'Label for the question thumbs up button.',
	},
	thumbsDownQuestion: {
		id: 'space-overview.smart-space-overview.popular-questions.thumbs-down',
		defaultMessage: 'Bad question',
		description: 'Label for the question thumbs down button.',
	},
	thumbsUpAnswer: {
		id: 'space-overview.smart-space-overview.popular-questions.thumbs-up-answer',
		defaultMessage: 'Good answer',
		description: 'Label for the answer thumbs up button.',
	},
	thumbsDownAnswer: {
		id: 'space-overview.smart-space-overview.popular-questions.thumbs-down-answer',
		defaultMessage: 'Bad answer',
		description: 'Label for the answer thumbs down button.',
	},
});

const styles = cssMap({
	cardWrapper: {
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		overflow: 'hidden',
	},
	questionRow: {
		position: 'relative',
		display: 'grid',
		gridTemplateColumns: 'auto 1fr auto auto',
		gridTemplateAreas: '"icon text feedback chevron"',
		alignItems: 'center',
		columnGap: token('space.100'),
		cursor: 'pointer',
		backgroundColor: token('elevation.surface.sunken'),
		borderWidth: '0',
		width: '100%',
		font: token('font.body'),
		color: token('color.text'),
		textAlign: 'start',
		'--question-feedback-opacity': '0',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			// @ts-expect-error -- CSS custom property not supported by cssMap types
			'--question-feedback-opacity': '1',
		},
	},
	questionRowExpanded: {
		borderRadius: '0',
	},
	questionRowCollapsed: {
		borderRadius: token('radius.xlarge'),
	},
	questionTrigger: {
		position: 'absolute',
		inset: token('space.0'),
		backgroundColor: 'transparent',
	},
	questionIcon: {
		gridArea: 'icon',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		paddingInlineStart: token('space.250'),
		pointerEvents: 'none',
	},
	questionText: {
		gridArea: 'text',
		position: 'relative',
		minWidth: '0px',
		paddingInline: token('space.100'),
		paddingBlock: token('space.200'),
		pointerEvents: 'none',
	},
	questionFeedbackButtons: {
		gridArea: 'feedback',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		flexShrink: 0,
	},
	chevron: {
		gridArea: 'chevron',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		paddingInlineEnd: token('space.250'),
		pointerEvents: 'none',
	},
	expandedContent: {
		backgroundColor: token('elevation.surface.sunken'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.250'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		'--answer-feedback-opacity': '0',
		'&:hover': {
			// @ts-expect-error -- CSS custom property not supported by cssMap types
			'--answer-feedback-opacity': '1',
		},
	},
	sectionLabel: {
		paddingBlockEnd: token('space.050'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	answerFeedbackButtons: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
	},
	hideQuestionFeedbackButtons: {
		transition: 'opacity 150ms ease-in',
		// @ts-expect-error -- CSS custom property not supported by cssMap types
		opacity: 'var(--question-feedback-opacity)',
	},
	hideAnswerFeedbackButtons: {
		transition: 'opacity 150ms ease-in',
		// @ts-expect-error -- CSS custom property not supported by cssMap types
		opacity: 'var(--answer-feedback-opacity)',
	},
});
